/** @jsxImportSource @emotion/react */
import React from 'react';
import logo from '../../assets/logo.svg';
import { keyframes } from '@emotion/react';

// Define the animation
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

// Add pulsing animation for the CTA buttons
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

function TikTokAccountSetupComponent() {
  return (
    <div className="min-h-screen bg-[#7B1C14] py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <img 
            src={logo} 
            alt="Spicy Cubes" 
            className="h-16 sm:h-20 md:h-24 mx-auto"
          />
        </div>
        
        <div className="bg-[#6E1912] rounded-lg shadow-lg p-8">
          <div className="relative pb-6 mb-6 border-b border-[#F7E8CC]/10">
            <h1 className="text-3xl font-bold text-[#F7E8CC] text-center">SOP: TikTok Account Setup</h1>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-48 h-[2px] bg-gradient-to-r from-transparent via-[#F7E8CC]/10 to-transparent" />
          </div>

          {/* Introduction */}
          <div className="mt-6 bg-[#5E150F]/30 p-4 rounded-md border border-[#FFD700]/30">
            <p className="text-[#F7E8CC] font-medium">
              Follow this step-by-step guide to set up your TikTok account for Spicy Cubes content. 
              A properly set up account will help you maximize reach and engagement.
            </p>
          </div>

          {/* Step 1 */}
          <div className="mt-6 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                1
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">Sign Up</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90">
                Create a <span className="font-bold text-[#FFD700]">BRAND NEW</span> TikTok account for your Spicy Cubes content.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                2
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">Username</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90 mb-2">
                Username examples:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-[#F7E8CC]/90">
                <li>tryspicycubes</li>
                <li>needspicycubes</li>
                <li>takespicycubes</li>
              </ul>
              <p className="mt-2 text-[#F7E8CC]/90">
                If you need help with your username, please reach out on Geneva.
              </p>
              <div className="mt-3 p-2 bg-[#5E150F]/50 rounded-md border border-[#FFD700]/30">
                <p className="text-[#FFD700] font-medium">TIP: Include "Spicy" in your display name</p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                3
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">Profile Photo</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90">
                Use any picture you would normally use for your social media profiles.
              </p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                4
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">Bio</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90 mb-3">
                Copy this for your bio, adding your discount code:
              </p>
              <div className="p-3 bg-[#5E150F]/50 rounded-md border border-[#FFD700]/30 font-medium text-[#F7E8CC]">
                Aphrodisiac gummies for two 💕<br/>
                <span className="italic">(*insert your discount code here*)</span> for 18% off<br/>
                #spicypartner<br/>
                Spicycubes.co 🔗
              </div>
              <p className="mt-3 text-[#F7E8CC]/90 italic">
                *Once your account reaches 1K followers, you will add your affiliate link to the links section*
              </p>
            </div>
          </div>

          {/* Step 5 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                5
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">Engagement (Warming Up Your Account)</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90 mb-2">
                Make sure you engage with other accounts so TikTok doesn't flag you as a bot:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-[#F7E8CC]/90">
                <li>Follow other creators (not Spicy Cubes creators, just other content creators)</li>
                <li>Repost their content, like videos, leave comments, etc. for <span className="text-[#FFD700] font-medium">~3 days</span> so TikTok knows you're a real user</li>
                <li>Avoid posting or performing any actions that might seem spammy during this time period</li>
              </ul>

              <div className="mt-4 p-3 bg-[#5E150F]/50 rounded-md border border-[#FFD700]/30">
                <h4 className="font-bold text-[#FFD700] mb-2">How to Know If the Account Is "Warmed":</h4>
                <p className="text-[#F7E8CC]/90 mb-2">
                  After 3 days of scrolling, commenting, and liking (without posting), the account should be warmed.
                </p>
                <p className="text-[#F7E8CC]/90 mb-1 font-medium">Test for warming:</p>
                <ol className="list-decimal pl-5 space-y-1 text-[#F7E8CC]/90">
                  <li>Scroll through your For You Page (FYP)</li>
                  <li>Like a video and watch 5–6 more videos</li>
                  <li>Scroll back to check if the liked video remains liked</li>
                  <li>If the likes stick, your account is warmed and ready for regular posting</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Comment Filtering */}
          <div className="mt-6 bg-[#5E150F]/70 p-5 rounded-md">
            <h3 className="font-bold text-lg mb-3 text-[#FFD700]">FILTER COMMENTS</h3>
            <p className="text-[#F7E8CC]/90 mb-3">
              Go to <span className="font-medium">Settings and Privacy → Privacy → Comments → Filter Specific Keywords</span>
            </p>
            
            <p className="text-[#F7E8CC]/90 mb-2">Filter these specific keywords:</p>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">satire</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">infomercial</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">commercial</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">branded</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">scams</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">scam</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">anything</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">ad</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">pitch</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">sale</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">sales</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">sell</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">came</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">never</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">paid</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">advertising</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">advertised</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">mlm</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">selling</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">promote</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">promo</div>
              <div className="bg-[#5E150F] p-2 rounded-md text-[#F7E8CC]/90">buy</div>
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-6 bg-[#5E150F]/30 p-4 rounded-md border border-[#FFD700]/30">
            <p className="text-center text-[#F7E8CC]/90">
              If you have any questions regarding account setup, reach out to Ashli on Geneva.
            </p>
          </div>
          
          <div className="mt-8 text-center">
            <a 
              href="/content-brief" 
              css={{
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
              className="inline-flex justify-center rounded-md bg-[#FFB700] py-3 px-6 text-lg font-bold text-[#5E150F] shadow-sm hover:bg-[#FFA200] focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:ring-offset-2 focus:ring-offset-[#7B1C14] transition-colors duration-200"
            >
              📱 Back to Content Brief 📱
            </a>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-[#F7E8CC]/80 text-center">
            Questions? Contact{' '}
            <a 
              href="mailto:ashli@spicycubes.co" 
              className="font-bold text-[#F7E8CC] hover:text-[#F7E8CC] whitespace-nowrap underline decoration-[2px] hover:decoration-[2.5px] transition-all duration-200 [text-decoration-skip-ink:none]"
            >
              ashli@spicycubes.co
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export const TikTokAccountSetup = TikTokAccountSetupComponent; 